<template>
  <div class="spectra-chart-container">
    <div class="controls-container">
      <label for="weight-select">Select Weighting:</label>
      <select id="weight-select" v-model="selectedWeight" @change="updateChart">
        <option value="A">A</option>
        <option value="C">C</option>
        <option value="Z">Z</option>
      </select>
    </div>
    <div v-if="!loaded" class="loading">
      <div class="lds-dual-ring-dashboard"></div>
      <span>Loading chart...</span>
    </div>
    <v-chart :option="chartOption" class="chart-container" autoresize />
  </div>
</template>

<script>
import VChart from "vue-echarts";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { BoxplotChart } from "echarts/charts";
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
} from "echarts/components";

use([
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  BoxplotChart,
  CanvasRenderer,
]);

import { api_config } from "@/env.ts";
import axios from "axios";
import { getToken } from "../keycloak-wrapper.ts";
import { bankersRound } from "../helpers";

export default {
  name: "WeightedSpectra",
  components: {
    VChart,
  },
  props: {
    APIparams: {
      type: Object,
      required: true,
    },
    grid: {
      type: Object,
      default: () => {
        return {
          left: 50,
          top: 10,
          right: 50,
          bottom: 40,
        };
      },
    },
  },
  data() {
    return {
      selectedWeight: "A",
      rawChartData: null,
      loaded: false,
      chartOption: {},
    };
  },
  methods: {
    async fetchData() {
      this.loaded = false;
      try {
        const token = await getToken();
        const response = await axios.post(
          api_config.backend + "/weightedSpectra",
          this.APIparams,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        // Assume response.data.spectra is an object with keys "A", "C", "Z"
        this.rawChartData = response.data.spectra;
        this.loaded = true;
        this.updateChart();
      } catch (error) {
        console.error("Error fetching weighted spectra:", error);
      }
    },
    updateChart() {
      if (!this.loaded) return;
      const dataForWeight = this.rawChartData[this.selectedWeight];
      if (!dataForWeight) return;

      // Assume dataForWeight is an object like:
      // { "100Hz": [value1, value2, ...], "125Hz": [value1, value2, ...], ... }
      // We need to construct a dataset source as a 2D array where the first column
      // is the frequency label and subsequent columns are the raw sample values.

      const frequencies = Object.keys(dataForWeight);

      // Determine the maximum number of samples among all frequency bands.
      let maxSamples = 0;
      frequencies.forEach((freq) => {
        const samples = Array.isArray(dataForWeight[freq])
          ? dataForWeight[freq]
          : [dataForWeight[freq]];
        if (samples.length > maxSamples) {
          maxSamples = samples.length;
        }
      });

      // Build the data rows.
      const source = [];
      frequencies.forEach((freq) => {
        const row = Object.keys(dataForWeight[freq]).map((key) =>
          bankersRound(dataForWeight[freq][key])
        );
        // Fill with nulls if there are missing sample values.
        while (row.length < frequencies.length) {
          row.push(null);
        }
        source.push(row);
      });

      // Set the chart option using the dataset and the boxplot transform.
      this.chartOption = {
        //   title: {
        //     text: `${this.selectedWeight} Weighted 1/3rd Octave Spectra Boxplot`
        //   },
        tooltip: {
          trigger: "item",
          axisPointer: {
            type: "shadow",
          },
          formatter: function (param) {
            // The transformed data from the boxplot transform is a five-number array.
            if (param.value instanceof Array && param.value.length === 6) {
              return [
                `Band: ${param.value[0]}`,
                `Min: ${bankersRound(param.value[1])}`,
                `Q1: ${bankersRound(param.value[2])}`,
                `Median: ${bankersRound(param.value[3])}`,
                `Q3: ${bankersRound(param.value[4])}`,
                `Max: ${bankersRound(param.value[5])}`,
              ].join("<br/>");
            }
            if (param.seriesType === "scatter") {
              return `${param.value[1]} `;
            }
            return param.value;
          },
        },
        dataset: [
          {
            // Raw data: [freq, data samples]
            source: source,
          },
          {
            // Transform the raw data to boxplot statistics.
            transform: {
              type: "boxplot",
              config: {
                itemNameFormatter: function (value) {
                  return `${frequencies[value.value]}`;
                },
              },
            },
          },
          {
            fromDatasetIndex: 1,
            fromTransformResult: 1,
          },
        ],
        xAxis: {
          type: "category",
          boundaryGap: true,
          // name: '1/3rd Octave Spectra Band',
          nameGap: 30,
        },
        yAxis: {
          type: "value",
          name: "dB",
          // Sets minimum Y axis value, causes strange clipping
          // Comment out while waiting response on preferred outcome from Clayton
          //min: 0,
        },
        grid: this.grid,
        series: [
          {
            name: "boxplot",
            type: "boxplot",
            datasetIndex: 1,
            itemStyle: {
              color: "#FFCB05",
              borderColor: "#009B69",
            },
          },
          {
            name: "outlier",
            type: "scatter",
            datasetIndex: 2,
            itemStyle: {
              color: "#B2D235",
            },
          },
        ],
      };
    },
  },
  mounted() {
    this.fetchData();
  },
};
</script>

<style scoped>
.weighted-spectra-container {
  padding: 10px;
  border: 2px solid var(--colour-border-strong);
  border-radius: 15px;
  background-color: var(--colour-bg-light);
  height: 100%;
  width: 100%;
}
.weighted-spectra-header-container {
  height: 80px;
}
.spectra-chart-container {
  width: 100%;
  height: calc(100%);
}
.chart-container {
  width: 100%;
  height: 100%;
}
.loading {
  text-align: center;
  margin-top: 10px;
}
</style>
